import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import fashionHero from '../assets/images/hero-fashion.png';

const slides = [
  {
    id: 1,
    tag: "Summer 26' Collection",
    title: "Fashion",
    subtitle: "Optometrists",
    desc: "Elegance in Every Detail",
    motto: "Siyane Editorial Selection"
  },
  {
    id: 2,
    tag: "Expert Care Path",
    title: "Clinical",
    subtitle: "Precision",
    desc: "Advanced Visionary Science",
    motto: "Expert Verified Diagnostics"
  },
  {
    id: 3,
    tag: "Designer Series",
    title: "Luxury",
    subtitle: "Collection",
    desc: "Curated Global Frames",
    motto: "Discover Your Signature"
  }
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative h-[90vh] lg:h-screen w-full overflow-hidden bg-slate-900 font-poppins">
      {/* Main Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src={fashionHero} 
          alt="Siyane High Fashion Eyewear" 
          className="w-full h-full object-cover object-center opacity-80"
        />
        {/* Cinematic Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/60 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent"></div>
      </div>

      {/* Hero Content Wrapper */}
      <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-between py-12 lg:py-24 z-10">
        
        {/* Top Editorial Branding (Fixed) */}
        <div className="max-w-xs space-y-4">
          <div className="space-y-1">
            <p className="text-[10px] lg:text-xs font-bold text-white/50 uppercase tracking-[0.4em]">
              Siyane Editorial <span className="mx-2 text-white/20">|</span> Estd. 1996
            </p>
          </div>
          <div className="w-12 h-[1px] bg-white/30"></div>
        </div>

        {/* Animated Slide Content */}
        <div className="relative flex-1 flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <p className="text-[10px] lg:text-xs font-bold text-syana-blue-400 uppercase tracking-[0.4em]">
                  {slides[currentSlide].tag}
                </p>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-light text-white tracking-widest uppercase leading-tight">
                  {slides[currentSlide].title} <br/>
                  <span className="font-extrabold">{slides[currentSlide].subtitle}</span>
                </h1>
                <p className="text-[10px] lg:text-sm font-medium tracking-[0.3em] text-white/60 italic">
                  — {slides[currentSlide].desc}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom Section: Branding & Indicators */}
        <div className="flex flex-col lg:flex-row items-end lg:items-center justify-between gap-8">
          
          {/* Main Headline & Secondary Branding */}
          <div className="hidden lg:block space-y-2">
             <AnimatePresence mode="wait">
               <motion.h2 
                 key={currentSlide}
                 initial={{ opacity: 0, y: 10 }}
                 animate={{ opacity: 1, y: 0 }}
                 exit={{ opacity: 0, y: -10 }}
                 className="text-xl lg:text-3xl font-bold text-white tracking-tighter leading-none"
               >
                 {slides[currentSlide].subtitle} <span className="font-light text-white/40 italic">Series.</span>
               </motion.h2>
             </AnimatePresence>
             <p className="text-[10px] text-white/50 tracking-[0.3em] uppercase font-bold">
               {slides[currentSlide].motto}
             </p>
          </div>

          {/* Slide Indicators */}
          <div className="flex space-x-4">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-1 transition-all duration-500 ${index === currentSlide ? 'w-12 bg-syana-blue-400' : 'w-4 bg-white/20'}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Frame Elements */}
      <div className="absolute top-12 right-12 w-32 h-32 border-t border-r border-white/10 -z-0"></div>
      <div className="absolute bottom-12 left-12 w-32 h-32 border-b border-l border-white/10 -z-0"></div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-4 opacity-30 animate-pulse">
        <div className="w-[1px] h-12 bg-gradient-to-b from-transparent via-white to-transparent"></div>
        <p className="text-[10px] font-medium text-white uppercase tracking-[0.3em] vertical-rl">Discovery</p>
      </div>
    </section>
  );
};

export default Hero;
